import React, { useMemo } from "react";

import {
  CalendarYearOptions,
  planDateTypeOptionsDropdown,
  SpouseOptions,
  SpousesAgeOptions,
  YourAgeOptions,
} from "../../constants";
import MultiDropdownInput from "../MultiDropdownInput";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";

const maxAge = 115;
const maxYearsInFuture = 100;
const placeholderYearsInTheFuture = 20;

interface Props {
  planDateFieldName: string;
  planDateFieldLabel: string;
  curPlanDateOption: string;
  isEndDate: boolean;

  input1FieldName: string;
  input1FieldLabel: string;
  input1Placeholder?: string;
  input1Required?: string;

  input2FieldName: string;
  input2FieldLabel: string;
  input2Placeholder?: string;
  input2Required?: string;

  input3FieldName: string;
  input3FieldLabel: string;
  input3Placeholder?: string;
  input3Required?: string;

  hideSpouseOptions?: boolean;
  isDisabled?: boolean;
}

const PlanDateMultiDropdownInput = ({
  planDateFieldName,
  planDateFieldLabel,
  curPlanDateOption,
  isEndDate,

  input1FieldName,
  input1FieldLabel,
  input1Placeholder = `${
    new Date().getFullYear() + placeholderYearsInTheFuture
  }`,
  input1Required = "Must not be empty",

  input2FieldName,
  input2FieldLabel,
  input2Placeholder = "64",
  input2Required = "Must not be empty",

  input3FieldName,
  input3FieldLabel,
  input3Placeholder = "65",
  input3Required = "Must not be empty",

  hideSpouseOptions = false,
  isDisabled = false,
}: Props) => {
  const dropdownOptions = useMemo(
    () =>
      planDateTypeOptionsDropdown
        .filter((el) => (isEndDate ? el.value !== "AlreadyStarted" : true))
        .filter((el) =>
          hideSpouseOptions
            ? SpouseOptions.every((so) => so.value !== el.value)
            : true
        )
        .map((tf) => ({
          value: tf.value,
          dateType: tf.dateType,
          label: tf.label,
        })),
    [hideSpouseOptions, isEndDate]
  );

  return (
    <MultiDropdownInput
      isDisabled={isDisabled}
      fieldName={planDateFieldName}
      fieldLabel={planDateFieldLabel}
      curOption={curPlanDateOption}
      options={dropdownOptions}
      displayInput1OnOptions={CalendarYearOptions}
      additionalInput1={
        <FormNumberField
          name={input1FieldName}
          label={input1FieldLabel}
          disabled={isDisabled}
          customRegex={{
            regex: /^[\d][\d][\d][\d]$/g,
            message: "Invalid year",
          }}
          placeholder={input1Placeholder}
          required={input1Required}
          rules={{
            required: true,
            validate: {
              isValidYear: (value): string | true => {
                const curYear = new Date().getFullYear();

                if (!value) {
                  return `Invalid value`;
                }

                const valueAsNum = parseInt(value);

                if (Number.isNaN(valueAsNum)) {
                  return `Invalid value`;
                }

                if (value > curYear + maxYearsInFuture || value < curYear) {
                  return `Invalid year`;
                }

                return true;
              },
            },
          }}
        />
      }
      displayInput2OnOptions={YourAgeOptions}
      additionalInput2={
        <FormNumberField
          name={input2FieldName}
          label={input2FieldLabel}
          disabled={isDisabled}
          customRegex={{
            regex: /^[\d]{1,3}$/g,
            message: "Must be only numerical digits",
          }}
          placeholder={input2Placeholder}
          required={input2Required}
          rules={{
            required: true,
            validate: {
              isValidAge: (value): string | true => {
                if (!value) {
                  return `Invalid value`;
                }

                const valueAsNum = parseInt(value);

                if (Number.isNaN(valueAsNum)) {
                  return `Invalid value`;
                }

                if (value < 0 || value > maxAge) {
                  return `Invalid age`;
                }

                return true;
              },
            },
          }}
        />
      }
      displayInput3OnOptions={SpousesAgeOptions}
      additionalInput3={
        <FormNumberField
          name={input3FieldName}
          label={input3FieldLabel}
          disabled={isDisabled}
          customRegex={{
            regex: /^[\d]{1,3}$/g,
            message: "Must be only numerical digits",
          }}
          placeholder={input3Placeholder}
          required={input3Required}
          rules={{
            required: true,
            validate: {
              isValidAge: (value): string | true => {
                if (!value) {
                  return `Invalid value`;
                }

                const valueAsNum = parseInt(value);

                if (Number.isNaN(valueAsNum)) {
                  return `Invalid value`;
                }

                if (value < 0 || value > maxAge) {
                  return `Invalid age`;
                }

                return true;
              },
            },
          }}
        />
      }
      fillUpSpaceIfNoInputsRendered={true}
    />
  );
};

export default PlanDateMultiDropdownInput;
